import React from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StorefrontIcon from "@material-ui/icons/Storefront";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import SecurityIcon from "@material-ui/icons/Security";
import PeopleIcon from "@material-ui/icons/People";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import { CUSTOMER_CARE_NUMBER } from "../constants";
import RestaurantIcon from "@material-ui/icons/Restaurant";
import LocalMallIcon from "@material-ui/icons/LocalMall";
import LocalPharmacyIcon from "@material-ui/icons/LocalPharmacy";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import StarIcon from "@material-ui/icons/Star";
// import LusukuLogo from "../assets/lusuku-logo.png";
import LusukuLogo from "../assets/lusuku_logo_main.png";
import {
  COUNTRY_NAME,
  NATIONALITY,
  OFFICE_ADDRESS,
  SUPPORT_EMAIL,
} from "../config/market";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const spinSlow = keyframes`
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.05);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
`;

// Styled Components
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #324d54 0%, #1a2a2e 50%, #0f1a1c 100%);
  padding: var(--lusuku-nav-h, 120px) 20px 140px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 80px;
    background: #ffffff;
    border-radius: 100% 100% 0 0;
    transform: translateY(50%);
  }

  @media (max-width: 768px) {
    padding: var(--lusuku-nav-h, 140px) 16px 120px;

    &::after {
      height: 50px;
    }
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const HeroTitle = styled.h1`
  color: white;
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 24px;
  line-height: 1.2;

  span {
    color: #ffc107;
  }

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.3rem;
  line-height: 1.8;
  max-width: 700px;
  margin: 0 auto 40px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.3);
  padding: 12px 24px;
  border-radius: 50px;
  color: #ffc107;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 30px;

  svg {
    font-size: 20px;
  }
`;

const Section = styled.section`
  padding: 80px 20px;
  background: ${(props) => props.$bg || "#ffffff"};

  @media (max-width: 768px) {
    padding: 60px 16px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 16px;

  span {
    color: #324d54;
  }

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.15rem;
  color: #666;
  text-align: center;
  max-width: 700px;
  margin: 0 auto 50px;
  line-height: 1.7;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const StorySection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const StoryContent = styled.div`
  h3 {
    font-size: 1.8rem;
    color: #1a1a1a;
    margin-bottom: 20px;
    font-weight: 700;
  }

  p {
    color: #555;
    line-height: 1.8;
    margin-bottom: 20px;
    font-size: 1.05rem;
  }
`;

const StoryImage = styled.div`
  background: linear-gradient(135deg, #324d54 0%, #1a2a2e 100%);
  border-radius: 24px;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 350px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle,
      rgba(255, 193, 7, 0.1) 0%,
      transparent 70%
    );
  }
`;

const LogoLarge = styled.div`
  font-size: 4rem;
  font-weight: 800;
  color: white;
  margin-bottom: 16px;
  animation: ${float} 3s ease-in-out infinite;

  span {
    color: #ffc107;
  }
`;

const SpinningLogoContainer = styled.div`
  position: relative;
  width: 190px;
  height: 190px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    width: 140px;
    height: 140px;
  }
`;

const SpinningLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  animation: ${spinSlow} 12s linear infinite;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.3));

  &:hover {
    animation: ${spin} 2s linear infinite;
  }
`;

const Tagline = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.1rem;
  text-align: center;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(50, 77, 84, 0.15);
    border-color: #324d54;
  }
`;

const ServiceIcon = styled.div`
  width: 70px;
  height: 70px;
  background: ${(props) =>
    props.$bg || "linear-gradient(135deg, #324D54 0%, #1a2a2e 100%)"};
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;

  svg {
    font-size: 32px;
    color: white;
  }
`;

const ServiceTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 12px;
`;

const ServiceDesc = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  text-align: center;
  padding: 30px 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatNumber = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #ffc107;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const StatLabel = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 500;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border-left: 4px solid ${(props) => props.$accent || "#324D54"};
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ValueTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: ${(props) => props.$accent || "#324D54"};
    font-size: 28px;
  }
`;

const ValueDesc = styled.p`
  color: #555;
  line-height: 1.7;
  font-size: 1rem;
`;

const BenefitsSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitColumn = styled.div`
  background: ${(props) => props.$bg || "white"};
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${(props) => props.$color || "#1a1a1a"};
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    font-size: 28px;
  }
`;

const BenefitList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  color: ${(props) => props.$color || "#555"};
  font-size: 1rem;
  line-height: 1.6;

  svg {
    color: ${(props) => props.$iconColor || "#10B981"};
    font-size: 20px;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #ffc107 0%, #ffb300 50%, #ff9800 100%);
  padding: 80px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E");
  }

  @media (max-width: 768px) {
    padding: 60px 16px;
  }
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const CTAText = styled.p`
  font-size: 1.2rem;
  color: #333;
  margin-bottom: 32px;
  line-height: 1.7;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 16px 32px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;

  ${(props) =>
    props.$primary
      ? `
    background: #324D54;
    color: white;
    
    &:hover {
      background: #1a2a2e;
      transform: translateY(-3px);
      box-shadow: 0 8px 25px rgba(50, 77, 84, 0.4);
    }
  `
      : `
    background: white;
    color: #324D54;
    border: 2px solid #324D54;
    
    &:hover {
      background: #324D54;
      color: white;
      transform: translateY(-3px);
    }
  `}
`;

const ContactInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 50px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ContactCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
`;

const ContactIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 16px;
`;

const ContactTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const ContactText = styled.p`
  color: #666;
  font-size: 0.95rem;
`;

const AboutUs = () => {
  return (
    <PageWrapper>
      <Navbar />

      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroBadge>
            <StarIcon /> {COUNTRY_NAME}'s Leading Multivendor Marketplace
          </HeroBadge>
          <HeroTitle>
            Welcome to <span>Lusuku</span>
          </HeroTitle>
          <HeroSubtitle>
            Your one-stop digital marketplace connecting you to restaurants,
            retailers, wholesalers, healthcare providers, and local vendors
            across {COUNTRY_NAME}. We're revolutionizing how {NATIONALITY}s
            shop, eat, and access essential services.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      {/* Our Story Section */}
      <Section>
        <Container>
          <StorySection>
            <StoryContent>
              <h3>Our Story</h3>
              <p>
                Lusuku was born from a simple yet powerful vision: to bridge the
                gap between local businesses and customers in {COUNTRY_NAME}. We
                recognized that talented vendors, hardworking restaurant owners,
                and dedicated healthcare providers often struggled to reach
                their potential customers in the digital age.
              </p>
              <p>
                Founded with a passion for empowering local commerce, Lusuku has
                grown into {COUNTRY_NAME}'s most comprehensive multivendor
                marketplace. We've built more than just an app – we've created
                an ecosystem where businesses thrive and customers enjoy
                unparalleled convenience.
              </p>
              <p>
                Today, we proudly serve thousands of customers daily, partnering
                with hundreds of vendors, restaurants, and healthcare providers
                to deliver quality products and services right to your doorstep.
              </p>
            </StoryContent>
            <StoryImage>
              <SpinningLogoContainer>
                <SpinningLogo src={LusukuLogo} alt="Lusuku Logo" />
              </SpinningLogoContainer>
              <LogoLarge>
                Lusu<span>ku</span>
              </LogoLarge>
              <Tagline>
                Connecting {COUNTRY_NAME}, One Delivery at a Time
              </Tagline>
            </StoryImage>
          </StorySection>
        </Container>
      </Section>

      {/* What We Offer Section */}
      <Section $bg="#f8f9fa">
        <Container>
          <SectionTitle>
            What We <span>Offer</span>
          </SectionTitle>
          <SectionSubtitle>
            From delicious meals to everyday essentials, healthcare products to
            wholesale goods – Lusuku brings everything you need under one
            digital roof.
          </SectionSubtitle>

          <ServicesGrid>
            <ServiceCard>
              <ServiceIcon $bg="linear-gradient(135deg, #fc5c65 0%, #eb3b5a 100%)">
                <RestaurantIcon />
              </ServiceIcon>
              <ServiceTitle>Restaurants</ServiceTitle>
              <ServiceDesc>
                Order from your favorite local restaurants. From traditional{" "}
                {NATIONALITY} cuisine to international flavors, satisfy every
                craving with quick delivery.
              </ServiceDesc>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon $bg="linear-gradient(135deg, #324D54 0%, #1a2a2e 100%)">
                <LocalMallIcon />
              </ServiceIcon>
              <ServiceTitle>Retail & Wholesale</ServiceTitle>
              <ServiceDesc>
                Shop groceries, electronics, fashion, and more from trusted
                retailers and wholesalers. Competitive prices with doorstep
                delivery.
              </ServiceDesc>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon $bg="linear-gradient(135deg, #10B981 0%, #059669 100%)">
                <LocalPharmacyIcon />
              </ServiceIcon>
              <ServiceTitle>Healthcare</ServiceTitle>
              <ServiceDesc>
                Access pharmaceutical products and healthcare essentials from
                licensed providers. Your health, delivered with care.
              </ServiceDesc>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon $bg="linear-gradient(135deg, #FFC107 0%, #FF9800 100%)">
                <StorefrontIcon />
              </ServiceIcon>
              <ServiceTitle>Local Vendors</ServiceTitle>
              <ServiceDesc>
                Discover unique products from local artisans and small
                businesses. Support your community while shopping conveniently.
              </ServiceDesc>
            </ServiceCard>
          </ServicesGrid>
        </Container>
      </Section>

      {/* Stats Section */}
      <Section $bg="linear-gradient(135deg, #324D54 0%, #1a2a2e 100%)">
        <Container>
          <SectionTitle style={{ color: "white" }}>
            Our <span style={{ color: "#FFC107" }}>Impact</span>
          </SectionTitle>
          <SectionSubtitle style={{ color: "rgba(255,255,255,0.8)" }}>
            Numbers that reflect our commitment to serving {COUNTRY_NAME}
          </SectionSubtitle>

          <StatsSection>
            <StatCard>
              <StatNumber>500+</StatNumber>
              <StatLabel>Active Vendors</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>50K+</StatNumber>
              <StatLabel>Happy Customers</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>100K+</StatNumber>
              <StatLabel>Orders Delivered</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>24/7</StatNumber>
              <StatLabel>Customer Support</StatLabel>
            </StatCard>
          </StatsSection>
        </Container>
      </Section>

      {/* Our Values Section */}
      <Section>
        <Container>
          <SectionTitle>
            Our <span>Values</span>
          </SectionTitle>
          <SectionSubtitle>
            The principles that guide everything we do at Lusuku
          </SectionSubtitle>

          <ValuesGrid>
            <ValueCard $accent="#fc5c65">
              <ValueTitle $accent="#fc5c65">
                <PeopleIcon /> Community First
              </ValueTitle>
              <ValueDesc>
                We believe in empowering local businesses and creating
                opportunities for
                {NATIONALITY} entrepreneurs. Every vendor on our platform is a
                partner in our mission to strengthen local commerce.
              </ValueDesc>
            </ValueCard>

            <ValueCard $accent="#324D54">
              <ValueTitle $accent="#324D54">
                <SecurityIcon /> Trust & Reliability
              </ValueTitle>
              <ValueDesc>
                Your trust is our foundation. We ensure secure transactions,
                verified vendors, and reliable delivery services. When you order
                through Lusuku, you can count on us.
              </ValueDesc>
            </ValueCard>

            <ValueCard $accent="#FFC107">
              <ValueTitle $accent="#FFC107">
                <TrendingUpIcon /> Innovation
              </ValueTitle>
              <ValueDesc>
                We continuously improve our platform to serve you better. From
                real-time order tracking to seamless payment options, we're
                always innovating for your convenience.
              </ValueDesc>
            </ValueCard>
          </ValuesGrid>
        </Container>
      </Section>

      {/* Benefits Section */}
      <Section $bg="#f8f9fa">
        <Container>
          <SectionTitle>
            Why Choose <span>Lusuku</span>?
          </SectionTitle>
          <SectionSubtitle>
            Whether you're a shopper looking for convenience or a vendor seeking
            growth, Lusuku has something special for you.
          </SectionSubtitle>

          <BenefitsSection>
            <BenefitColumn>
              <BenefitTitle>
                <LocalMallIcon style={{ color: "#324D54" }} /> For Shoppers
              </BenefitTitle>
              <BenefitList>
                <BenefitItem>
                  <CheckCircleIcon />
                  Wide variety of products from trusted vendors across{" "}
                  {COUNTRY_NAME}
                </BenefitItem>
                <BenefitItem>
                  <CheckCircleIcon />
                  Real-time order tracking – know exactly where your order is
                </BenefitItem>
                <BenefitItem>
                  <CheckCircleIcon />
                  Multiple payment options: Mobile Money, Card, Cash on Delivery
                </BenefitItem>
                <BenefitItem>
                  <CheckCircleIcon />
                  Fast and reliable delivery to your doorstep
                </BenefitItem>
                <BenefitItem>
                  <CheckCircleIcon />
                  Easy-to-use mobile app and web platform
                </BenefitItem>
                <BenefitItem>
                  <CheckCircleIcon />
                  Exclusive deals and promotions from your favorite vendors
                </BenefitItem>
                <BenefitItem>
                  <CheckCircleIcon />
                  Dedicated customer support ready to help
                </BenefitItem>
              </BenefitList>
            </BenefitColumn>

            <BenefitColumn $bg="linear-gradient(135deg, #324D54 0%, #1a2a2e 100%)">
              <BenefitTitle $color="white">
                <StorefrontIcon style={{ color: "#FFC107" }} /> For Vendors
              </BenefitTitle>
              <BenefitList>
                <BenefitItem
                  $color="rgba(255,255,255,0.9)"
                  $iconColor="#FFC107"
                >
                  <CheckCircleIcon />
                  Reach thousands of customers across {COUNTRY_NAME} instantly
                </BenefitItem>
                <BenefitItem
                  $color="rgba(255,255,255,0.9)"
                  $iconColor="#FFC107"
                >
                  <CheckCircleIcon />
                  Powerful vendor dashboard to manage orders and inventory
                </BenefitItem>
                <BenefitItem
                  $color="rgba(255,255,255,0.9)"
                  $iconColor="#FFC107"
                >
                  <CheckCircleIcon />
                  Flexible commission structure that supports your growth
                </BenefitItem>
                <BenefitItem
                  $color="rgba(255,255,255,0.9)"
                  $iconColor="#FFC107"
                >
                  <CheckCircleIcon />
                  Marketing support to boost your visibility
                </BenefitItem>
                <BenefitItem
                  $color="rgba(255,255,255,0.9)"
                  $iconColor="#FFC107"
                >
                  <CheckCircleIcon />
                  Reliable delivery network – we handle the logistics
                </BenefitItem>
                <BenefitItem
                  $color="rgba(255,255,255,0.9)"
                  $iconColor="#FFC107"
                >
                  <CheckCircleIcon />
                  Real-time analytics to understand your customers
                </BenefitItem>
                <BenefitItem
                  $color="rgba(255,255,255,0.9)"
                  $iconColor="#FFC107"
                >
                  <CheckCircleIcon />
                  Dedicated vendor support team for your success
                </BenefitItem>
              </BenefitList>
            </BenefitColumn>
          </BenefitsSection>
        </Container>
      </Section>

      {/* Technology Section */}
      <Section>
        <Container>
          <SectionTitle>
            Powered by <span>Technology</span>
          </SectionTitle>
          <SectionSubtitle>
            Our cutting-edge platform ensures a seamless experience for everyone
          </SectionSubtitle>

          <ServicesGrid>
            <ServiceCard>
              <ServiceIcon $bg="linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)">
                <PhoneAndroidIcon />
              </ServiceIcon>
              <ServiceTitle>Mobile Apps</ServiceTitle>
              <ServiceDesc>
                Download our app on iOS and Android for the best shopping
                experience. Order on the go, anytime, anywhere.
              </ServiceDesc>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon $bg="linear-gradient(135deg, #EC4899 0%, #DB2777 100%)">
                <LocalShippingIcon />
              </ServiceIcon>
              <ServiceTitle>Live Tracking</ServiceTitle>
              <ServiceDesc>
                Track your orders in real-time from preparation to delivery.
                Know exactly when your order will arrive.
              </ServiceDesc>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon $bg="linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)">
                <SecurityIcon />
              </ServiceIcon>
              <ServiceTitle>Secure Payments</ServiceTitle>
              <ServiceDesc>
                Multiple secure payment options including MTN MoMo, Airtel
                Money, Visa/Mastercard, and Cash on Delivery.
              </ServiceDesc>
            </ServiceCard>

            <ServiceCard>
              <ServiceIcon $bg="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)">
                <TrendingUpIcon />
              </ServiceIcon>
              <ServiceTitle>Smart Analytics</ServiceTitle>
              <ServiceDesc>
                Vendors get powerful insights to grow their business. Customers
                get personalized recommendations.
              </ServiceDesc>
            </ServiceCard>
          </ServicesGrid>
        </Container>
      </Section>

      {/* CTA Section */}
      <CTASection>
        <CTAContent>
          <CTATitle>Ready to Experience Lusuku?</CTATitle>
          <CTAText>
            Join thousands of satisfied customers and vendors who have made
            Lusuku their trusted marketplace. Start shopping or grow your
            business with us today!
          </CTAText>
          <CTAButtons>
            <CTAButton to="/" $primary>
              <LocalMallIcon /> Start Shopping
            </CTAButton>
            <CTAButton
              as="a"
              href="https://lusuku.shop/vendorRegistration"
              target="_blank"
              rel="noopener noreferrer"
            >
              <StorefrontIcon /> Become a Vendor
            </CTAButton>
          </CTAButtons>
        </CTAContent>
      </CTASection>

      {/* Contact Section */}
      <Section>
        <Container>
          <SectionTitle>
            Get in <span>Touch</span>
          </SectionTitle>
          <SectionSubtitle>
            We'd love to hear from you. Reach out to us anytime!
          </SectionSubtitle>

          <ContactInfo>
            <ContactCard>
              <ContactIcon>📍</ContactIcon>
              <ContactTitle>Visit Us</ContactTitle>
              <ContactText>
                {OFFICE_ADDRESS.map((line) => (
                  <React.Fragment key={line}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </ContactText>
            </ContactCard>

            <ContactCard>
              <ContactIcon>📞</ContactIcon>
              <ContactTitle>Call Us</ContactTitle>
              <ContactText>
                Helpline: {CUSTOMER_CARE_NUMBER}
                <br />
                {/* WhatsApp: +256 700 771 301 */}
              </ContactText>
            </ContactCard>

            <ContactCard>
              <ContactIcon>✉️</ContactIcon>
              <ContactTitle>Email Us</ContactTitle>
              <ContactText>
                {SUPPORT_EMAIL}
                <br />
                {/* vendors@lusuku.ug */}
              </ContactText>
            </ContactCard>
          </ContactInfo>
        </Container>
      </Section>

      <Footer />
    </PageWrapper>
  );
};

export default AboutUs;
